import React, { Fragment } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { Controller } from "react-hook-form";
import { AssessmentPaneProps } from "./types";
import { ActionsContainer, Button } from "../../components";
import { RadioButton } from "react-native-paper";
import SuccessModal from "./SuccessModal";
import { FailModal } from "./FailModal";
import { AttemptLeftModal } from "./AttemptLeftModal";
import { Assessment } from "./types";

export default function AssessmentPane({
  videoData,
  control,
  errors,
  handleAnswerChange,
  handleSubmit,
  onSubmit,
  handleOnSubmitError,
  loading,
  apiError,
  allAnswered,
  modalVisible,
  assessmentResult,
  onCloseAssessment,
  onCloseModal,
  Divider,
}: AssessmentPaneProps) {
  const assessment = videoData.assessment as Assessment;

  const renderModalContent = () => {
    if (!assessmentResult) return null;

    if (assessmentResult.assessmentAttempt.status === "passed") {
      return (
        <SuccessModal
          assessmentResult={assessmentResult.assessmentAttempt}
          visible={modalVisible}
          onCloseAssessment={onCloseAssessment}
        />
      );
    } else if (assessmentResult.assessmentAttempt.status === "failed" && assessmentResult.hasAttemptsRemaining) {
      return (
        <AttemptLeftModal
          assessmentResult={assessmentResult.assessmentAttempt}
          onCloseModal={onCloseModal}
          onCloseAssessment={onCloseAssessment}
          visible={modalVisible}
        />
      );
    } else {
      return (
        <FailModal
          assessmentResult={assessmentResult.assessmentAttempt}
          onCloseAssessment={onCloseAssessment}
          visible={modalVisible}
        />
      );
    }
  };

  return (
    <>
      <View className="flex px-6 pb-6">
        <Text className="text-large-normal font-700 text-ink-darkest pb-4">Assessment</Text>
        <Text className="pb-4 text-ink-base">A score of 70% or higher is required to earn 1 PDR Credit.</Text>
        <Text className="text-ink-base">
          You will have two (2) attempts to achieve a passing score. If a passing score is not achieved on either attempt, no
          PDR Credits will be earned.
        </Text>
      </View>
      <View className="pt-4">
        {assessment.questions.map((question, qIndex) => (
          <Fragment key={question.id}>
            <Divider field={`answers.${qIndex}.question_option_ids`} />
            <Text className="pt-8 px-6 text-ink">
              Question {qIndex + 1} of {assessment.questions.length}
            </Text>
            <View className="py-2 px-6 pb-4">
              <Text className="text-large-normal font-700 text-ink-darkest">{question.content}</Text>
            </View>
            {errors.answers?.[qIndex]?.question_option_ids && (
              <Text className="text-red-dark pb-4 pl-6">{errors?.answers?.[qIndex]?.question_option_ids?.message}</Text>
            )}
            <Controller
              name={`answers.${qIndex}.question_option_ids`}
              control={control}
              rules={{ required: "Select an answer to continue." }}
              render={({ field: { value } }) => (
                <View className="px-6 pb-6">
                  {question.options?.map((option) => (
                    <TouchableOpacity
                      key={option.id}
                      onPress={() => {
                        handleAnswerChange(qIndex, [option.id]);
                      }}
                      className={`flex flex-row rounded-lg mb-3 ${
                        value?.includes(option.id)
                          ? "border-primary-lighter border-2 p-3"
                          : errors.answers?.[qIndex]?.question_option_ids
                          ? "border-red-dark border p-[13px]"
                          : "border-sky-dark border p-[13px]"
                      }`}
                    >
                      <View className="self-center mr-2">
                        <RadioButton.Android
                          value={option.id}
                          status={value?.includes(option.id) ? "checked" : "unchecked"}
                          onPress={() => {
                            handleAnswerChange(qIndex, [option.id]);
                          }}
                          testID={`radio-button-${option.id}`}
                        />
                      </View>
                      <View className="flex-1 self-center">
                        <Text className="text-regular-normal leading-tight">{option.content}</Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            />
          </Fragment>
        ))}
        <View>
          <ActionsContainer errorAlert errorMessage={apiError}>
            <Button
              loading={loading}
              disabled={loading}
              onPress={handleSubmit(onSubmit, handleOnSubmitError)}
              mode="contained"
              isValid={allAnswered}
            >
              Submit Answers
            </Button>
          </ActionsContainer>
        </View>
      </View>
      {renderModalContent()}
    </>
  );
}
